import { DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { useToast } from '@retail/hooks';
import { useFetchAssortmentTags } from '@retail/my-assortment/data-access';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { AssortmentTagChanges } from '@retail/my-assortment/context';
import { AssortmentItemMinimal, AssortmentTag } from '@retail/my-assortment/types';
import { AssortmentTagSelection } from '@retail/products/components';
import { SupplierItemAssortmentTag } from '@retail/products/types';
import { getActiveAssortmentTags } from '@retail/products/utils';
import { getISO_8601Date } from '@retail/utils';
import { ContainedButton, Dialog, TextButton } from '@shared/components';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';

interface Props {
  open: boolean;
  item: AssortmentItemMinimal;
  assortmentId: number;
  isSaving?: boolean;
  updateAssortmentTags: (changes: AssortmentTagChanges) => Promise<void>;
  onClose: () => void;
}

const toISODate = (date?: Date) => (date ? getISO_8601Date(date) : undefined);

function EditItemAssortmentTagsDialog({
  open,
  item,
  assortmentId,
  isSaving,
  updateAssortmentTags,
  onClose,
}: Props) {
  const t = useMyAssortmentTFunction();
  const toast = useToast();
  const { isBrandContext } = useSelectedOrgUnit();

  const { data: assortmentTagDtos = [] } = useFetchAssortmentTags({
    assortmentIds: [assortmentId],
  });
  const assortmentTags = useMemo(
    () => assortmentTagDtos.map(AssortmentTag.fromDTO),
    [assortmentTagDtos]
  );

  const persistedTags = useMemo(
    () =>
      getActiveAssortmentTags(item.assortmentTags).map((tag) =>
        tag.validFrom < new Date()
          ? new SupplierItemAssortmentTag({ ...tag, validFrom: new Date() })
          : tag
      ),
    [item.assortmentTags]
  );
  const [selectedTags, setSelectedTags] = useState<SupplierItemAssortmentTag[]>(persistedTags);

  const addTag = useCallback(
    (tag: AssortmentTag) =>
      setSelectedTags((prev) => [
        ...prev,
        new SupplierItemAssortmentTag({ tagId: tag.id, tagName: tag.name, validFrom: new Date() }),
      ]),
    []
  );

  const removeTag = useCallback(
    (tagId: number) => setSelectedTags((prev) => prev.filter((tag) => tag.tagId !== tagId)),
    []
  );

  const updateTagDates = useCallback(
    (tagId: number, { validFrom, validTo }: { validFrom: Date; validTo?: Date }) =>
      setSelectedTags((prev) =>
        prev.map((tag) =>
          tag.tagId === tagId ? new SupplierItemAssortmentTag({ ...tag, validFrom, validTo }) : tag
        )
      ),
    []
  );

  const hasInvalidDates = selectedTags.some(
    (tag) => !!tag.validTo && dayjs(tag.validTo).isBefore(tag.validFrom, 'day')
  );

  const onSubmit = useCallback(() => {
    const persistedTagsById = new Map(persistedTags.map((tag) => [tag.tagId, tag]));
    const selectedTagIds = new Set(selectedTags.map((tag) => tag.tagId));
    const supplierItemIds = [item.mgSupplierItemId];

    // Only post what changed: new tags, and existing tags whose dates were edited
    const changedTags = selectedTags.filter((tag) => {
      const persistedTag = persistedTagsById.get(tag.tagId);
      if (!persistedTag) return true;
      return (
        isBrandContext &&
        (toISODate(tag.validFrom) !== toISODate(persistedTag.validFrom) ||
          toISODate(tag.validTo) !== toISODate(persistedTag.validTo))
      );
    });

    updateAssortmentTags({
      updates: changedTags.map((tag) => ({
        tagId: tag.tagId,
        supplierItemIds,
        validFrom: getISO_8601Date(isBrandContext ? tag.validFrom : new Date()),
        validTo: isBrandContext ? toISODate(tag.validTo) : undefined,
      })),
      removals: persistedTags
        .filter((tag) => !selectedTagIds.has(tag.tagId))
        .map((tag) => ({ tagId: tag.tagId, supplierItemIds })),
    })
      .then(onClose)
      .catch((e) => {
        console.error(e);
        toast.error(t('myAssortment.actions.genericActionError'));
      });
  }, [
    isBrandContext,
    item.mgSupplierItemId,
    onClose,
    persistedTags,
    selectedTags,
    t,
    toast,
    updateAssortmentTags,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ '& .MuiPaper-root': { minHeight: 400 } }}
    >
      <DialogTitle>{t('myAssortment.actions.editTags')}</DialogTitle>

      <DialogContent>
        <Stack gap={3} pb={1}>
          <Stack>
            <Typography variant="subtitle1" fontWeight={600}>
              {item.primaryText}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`${item.mgItemNumber} · ${item.supplier.name}`}
            </Typography>
          </Stack>

          <AssortmentTagSelection
            assortmentTags={assortmentTags}
            selectedTags={selectedTags}
            addTag={addTag}
            removeTag={removeTag}
            updateTagDates={isBrandContext ? updateTagDates : undefined}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <TextButton onClick={onClose}>{t('myAssortment.cancel')}</TextButton>
        <ContainedButton
          size="small"
          color="secondary"
          disabled={hasInvalidDates}
          loading={isSaving}
          onClick={onSubmit}
        >
          {t('myAssortment.save')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(EditItemAssortmentTagsDialog);
